import { SharedModule } from '@/app/shared/shared.module';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  CropHealthAnalysisViewModelResponseResult,
  CropHealthDatesViewModelResponseResult,
  CropHealthProxy,
  CropHealthViewModel,
  CropHealthViewModelResponseResult,
  GddAnalysisDataViewModel,
  GroupedPlantationViewModel,
  LookupProxy,
  MapDistributionView,
  NoteProxy,
  NoteViewModel,
  PrecipitationAnalysisDataViewModel,
  SatelliteAnnualNdmiAnalysisData,
  SatelliteAnnualNdviAnalysisData,
  SatelliteProblematicSpotView,
  SatelliteViewModel,
} from '@/app/proxy/proxies';
import { DateTime } from 'luxon';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { PlantationService } from '@/app/core/services/plantation.service';
import { LngLat } from 'mapbox-gl';

import { MixpanelTrackerService } from '@/app/core/analytics';
import { ToastService } from '@/app/core/services/toast.service';
import { MapMessageTypeEnum } from './components/crop-health/crop-health-map/crop-health-map-message-overlay/crop-health-map-message-overlay.component';
import { CropHealthMapComponent } from './components/crop-health/crop-health-map/crop-health-map.component';
import { CropHealthNoteAllComponent } from './components/crop-health/crop-health-notes/crop-health-note-all/crop-health-note-all.component';
import { CropHealthNoteFormComponent } from './components/crop-health/crop-health-notes/crop-health-note-form/crop-health-note-form.component';
import { CropHealthNoteListComponent } from './components/crop-health/crop-health-notes/crop-health-note-list/crop-health-note-list.component';
import { CropHealthNoteViewComponent } from './components/crop-health/crop-health-notes/crop-health-note-view/crop-health-note-view.component';
import { HeatAccumulationComponent } from './components/heat-acumulation/heat-accumulation.component';
import { NdviNdmiDistributionComponent } from './components/ndvi-ndmi-distribution/ndvi-ndmi-distribution.component';
import { PlantWaterStressComponent } from './components/plant-water-stress/plant-water-stress.component';
import { RainfallSummaryComponent } from './components/rainfall-summary/rainfall-summary.component';
import { VegetationHealthComponent } from './components/vegetation-health/vegetation-health.component';
import { CropHealthMode, CropHealthService } from './services/crop-health.service';
import { CropHealthMapType } from './components/crop-health/crop-health-map/crop-health-map-footer/crop-health-map-footer.component';
import { PermissionService } from '@/app/core/services/permission.service';
import { PhenologyComponent } from './components/phenology/phenology.component';

@Component({
  selector: 'app-crop-monitoring',
  imports: [
    SharedModule,
    CropHealthMapComponent,
    CropHealthNoteListComponent,
    CropHealthNoteFormComponent,
    CropHealthNoteViewComponent,
    VegetationHealthComponent,
    NdviNdmiDistributionComponent,
    HeatAccumulationComponent,
    PlantWaterStressComponent,
    RainfallSummaryComponent,
    CropHealthNoteAllComponent,
    PhenologyComponent
  ],
  templateUrl: './crop-monitoring.component.html',
  styleUrl: './crop-monitoring.component.less',
  providers: [CropHealthProxy, NoteProxy],
})
export class CropMonitoringComponent implements OnInit, OnDestroy {
  selectedPlantation: GroupedPlantationViewModel;

  @ViewChild(CropHealthMapComponent, { static: true }) cropHealthMapComponent: CropHealthMapComponent;

  selectedNoteForView: NoteViewModel;

  mapInstance: mapboxgl.Map;

  cropHealthData: CropHealthViewModel;
  availableDates: DateTime<boolean>[] = [];
  selectedMapType: CropHealthMapType = {
    key: 'ndvi',
    label: 'doktar_app.services.crop_monitoring.ndvi_map_title',
    labelAbreviation: 'NDVI',
    value: 0,
    description: 'doktar_app.services.crop_monitoring.ndvi_map_description',
  };

  ndviAnnualAnalysis: SatelliteAnnualNdviAnalysisData[] = [];
  ndmiAnnualAnalysis: SatelliteAnnualNdmiAnalysisData[] = [];
  gddAnalysisData: GddAnalysisDataViewModel[] = [];
  precipitationAnalysisData: PrecipitationAnalysisDataViewModel[] = [];

  cropHealthLoading = false;
  cropHealthAnalysisLoading = false;
  noteLoading = false;

  selectedDay: SatelliteViewModel;
  ndviNdmiDistributionData: MapDistributionView[];
  // null => not computed yet for the current selected day
  noAnalysisIssueState: { date: string; visible: boolean } | null = null;

  problematicSpots: SatelliteProblematicSpotView[] = [];

  viewNoteSource: 'map' | 'seeAllNotes' | null = null;

  mode: CropHealthMode = 'reset';

  mapFullscreen = false;

  private destroy$ = new Subject<void>();
  private lastProblemSpotSeenEventKey: string | null = null;

  constructor(
    private cropHealthProxy: CropHealthProxy,
    private noteProxy: NoteProxy,
    private lookupProxy: LookupProxy,
    private plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService,
    private toastService: ToastService,
    public cropHealthService: CropHealthService,
    public permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    this.cropHealthService.selectedDay$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res?.day) {
        this.selectedDay = res.day;
        this.noAnalysisIssueState = null;
        this.updateMessageOverlay(res.day);
        this.onChangeDay(res.day);
      }
    });

    this.cropHealthService.mode$.pipe(takeUntil(this.destroy$)).subscribe(mode => {
      this.mode = mode;

      setTimeout(() => {
        this.cropHealthMapComponent.mapInstance?.resize();
      }, 100);
    });

    this.cropHealthService.selectedMapType$.pipe(takeUntil(this.destroy$)).subscribe(type => {
      if (type) {
        this.selectedMapType = type;
        if (this.cropHealthData) {
          const mapList = this.cropHealthData.mapList.filter(e => e.cropMapTypes === this.selectedMapType?.value);
          if (mapList?.length > 0) {
            const day = mapList.find(
              (e) => e.imageDate.hasSame(this.selectedDay?.imageDate, 'day')
            ) ?? mapList[0];
            const problematicSpots = this.cropHealthData?.problematicSpotView?.filter((e) =>
              e.date.hasSame(day.imageDate, 'day')
            );
            this.cropHealthService.setSelectedDay({ day, problematicSpots });
          }
        }
      }
    });
  }

  updateMessageOverlay(day: SatelliteViewModel) {
    if (day.isCloudly) {
      this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.cloudyWeather);
      return;
    } else {
      const problematicSpot = this.cropHealthData?.problematicSpotView?.find((e) =>
        e.date.hasSame(day.imageDate, 'day')
      );

      if (this.selectedMapType?.key === 'risk') {
        if (problematicSpot) {
          if (problematicSpot.problematicSpotStatus === 'Not Checked Yet') {
            this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.problematicSpot);
            return;
          }
        } else {
          const canSeeProblematicSpot = this.plantationService.selectedPlantation.isPremium || this.plantationService.selectedPlantation.hasPlanetImageryAccess || this.plantationService.selectedPlantation.isSentinelProblematicSpotPreferred;
          if (canSeeProblematicSpot) {
            this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.noProblem);
            return;
          } else {
            this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.permiumRequired);
            return;
          }
        }
      }
    }

    this.cropHealthService.setMapOverlayMessage(undefined);
  }

  private trackServicesProblemSpotSeen(day: SatelliteViewModel): void {
    if (!this.selectedPlantation || !day?.imageDate) {
      return;
    }

    const spots = this.cropHealthData?.problematicSpotView?.filter((item) =>
      item.date.hasSame(day.imageDate, 'day')
    ) ?? [];

    if (spots.length === 0) {
      return;
    }

    const eventKey = `${this.selectedPlantation.id}-${this.selectedMapType?.key}-${day.imageDate.toISODate()}-${spots.length}`;
    if (this.lastProblemSpotSeenEventKey === eventKey) {
      return;
    }
    this.lastProblemSpotSeenEventKey = eventKey;

    const land = this.plantationService.landList?.find((item) =>
      item.plantations?.some((plantation) => plantation.id === this.selectedPlantation.id)
    );
    const enterpriseIdFromLand = (land as any)?.enterpriseId as string | undefined;
    const enterpriseId = enterpriseIdFromLand || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this.mixpanelTracker.trackServicesProblemSpotSeen({
      'number of problem spot': spots.length,
      // NOTE: Append a zero-width space so Mixpanel doesn't coerce it into a Date/Datetime.
      // It still renders like "YYYY-MM-DD" in the UI.
      date: `${day.imageDate.toISODate()}\u200B`,
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  onMapReady(event: mapboxgl.Map) {
    this.mapInstance = event;

    this.lookup();
    this.plantationService.selectedPlantation$.pipe(takeUntil(this.destroy$)).subscribe((selectedPlantation) => {
      this.cropHealthData = undefined;
      this.availableDates = [];
      this.ndmiAnnualAnalysis = [];
      this.ndviAnnualAnalysis = [];
      this.gddAnalysisData = [];
      this.precipitationAnalysisData = [];
      this.cropHealthService.setNotes([]);
      if (selectedPlantation) {
        this.selectedPlantation = selectedPlantation;

        this.getCropHealthData();
        this.getCropHealthAnalysisData();
        this.getNotes();
        this.getAvailableDates();
      }
    });
  }

  getCropHealthData(startDate?: DateTime<boolean>, endDate?: DateTime<boolean>, loadMore = false, selectedDate?: DateTime<boolean>) {
    // when loadMore is true, it means that the user clicked on LoadMore Button
    // OR a date (that hasn't been loaded yet) was selected from calender
    // then new data will be appended to the existing data
    if (!loadMore) {
      // it means that the data is loaded for the first time
      this.cropHealthData = undefined;
    }
    this.cropHealthLoading = true;
    this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.loading)
    this.cropHealthProxy
      .cropHealth_GetCropHealth(this.selectedPlantation.id, startDate, endDate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.cropHealthLoading = false;
          this.cropHealthService.setMapOverlayMessage(undefined);
        })
      )
      .subscribe({
        next: (response: CropHealthViewModelResponseResult) => {
          if (response?.result) {
            let cropHealthData: CropHealthViewModel;
            if (!loadMore) {
              // if the data is loaded for first time keep the spots returned by the API
              cropHealthData = {
                ...response.result,
                problematicSpotView: response.result.problematicSpotView ?? [],
              } as CropHealthViewModel;
            } else {
              // if the user clicked on load more button or selected a date
              if (response?.result?.mapList?.length > 0) {
                // append new data to the existing data
                cropHealthData = {
                  ...this.cropHealthData,
                  mapList: [...this.cropHealthData.mapList, ...response.result?.mapList ?? []],
                  problematicSpotView: [
                    ...(this.cropHealthData.problematicSpotView ?? []),
                    ...(response.result.problematicSpotView ?? []),
                  ],
                } as CropHealthViewModel;
              }
            }

            // sort images DESC based on imageDate
            cropHealthData.mapList = cropHealthData?.mapList?.sort(
              (a, b) => b.imageDate.toMillis() - a.imageDate.toMillis()
            );

            // apply timezone offset
            cropHealthData.mapList = cropHealthData.mapList?.map((item) => {
              item.imageDate = item.imageDate.setZone(`UTC${this.selectedPlantation.offSet}`);
              return item;
            }) ?? [];

            // clean up image URLs to remove any query parameters
            if (cropHealthData?.mapList?.length > 0) {
              for (const item of cropHealthData.mapList) {
                item.imageUrl = item.imageUrl?.split('??')[0] as string;
              }
            }

            cropHealthData.problematicSpotView = cropHealthData.problematicSpotView?.map((item) => {
              item.date = item.date.setZone(`UTC${this.selectedPlantation.offSet}`);
              return item;
            });

            this.cropHealthData = cropHealthData;
            // this.cropHealthService.setCropHealthData(this.cropHealthData);

            // if a date was selected from calender then the we need to point the day on the swiper
            if (selectedDate) {
              const day = this.cropHealthData.mapList.find(e => e.cropMapTypes === this.selectedMapType?.value && e.imageDate.hasSame(selectedDate, 'day'));
              if (day) {

                setTimeout(() => {
                  // this.cropHealthMapComponent.cropHealthMapCalendarComponent.setSlide(day);
                  this.cropHealthService.setSelectedDay({
                    day, problematicSpots: this.cropHealthData?.problematicSpotView?.filter((e) =>
                      e.date.hasSame(day.imageDate, 'day')
                    )
                  });
                }, 500);
              }
            } else {
              if (this.selectedPlantation.isPremium) {
                /*
                 * If the images of 7 latest days contains a spot, the map should be opened with the Risk Map.
                 * If the day of spots are older than latest 7 days, the NDVI Map should be displayed.
                 */
                const sevenDaysAgo = DateTime.now().minus({ days: 8 });
                const spots = this.cropHealthData.problematicSpotView
                  .filter((e) => {
                    const d = e.date as DateTime; // your DateTime
                    return d >= sevenDaysAgo && d <= DateTime.now();
                  })
                  .sort((a, b) => b.date.toMillis() - a.date.toMillis());

                if (spots?.length > 0) {
                  this.cropHealthService.setSelectedMapType('risk');
                  const day = this.cropHealthData.mapList.find(e => e.cropMapTypes === 0 && e.imageDate.hasSame(spots[0].date, 'day'));
                  if (day) {
                    setTimeout(() => {
                      this.cropHealthService.setSelectedDay({ day, problematicSpots: spots })
                    }, 500);
                  }
                }
              }
            }
          }
        },
      });
  }

  getAvailableDates() {
    this.availableDates = [];
    this.cropHealthService.setAvailableDates(this.availableDates);

    this.cropHealthLoading = true;
    this.cropHealthProxy
      .cropHealth_GetCropHealthDates(this.selectedPlantation.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.cropHealthLoading = false;
        })
      )
      .subscribe({
        next: (response: CropHealthDatesViewModelResponseResult) => {
          const availableDates = response?.result?.imageDateList ?? [];
          this.availableDates = availableDates.map((e) => {
            return e.setZone(`UTC${this.plantationService.selectedPlantation?.offSet}`);
          }).sort((a, b) => b.toMillis() - a.toMillis()); // sort DESC

          this.cropHealthService.setAvailableDates(this.availableDates);
        },
      });
  }

  getCropHealthAnalysisData() {
    this.ndmiAnnualAnalysis = [];
    this.ndviAnnualAnalysis = [];
    this.gddAnalysisData = [];
    this.precipitationAnalysisData = [];

    this.cropHealthAnalysisLoading = true;
    this.cropHealthProxy
      .cropHealth_GetCropHealthAnalysis(this.selectedPlantation.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.cropHealthAnalysisLoading = false;
        })
      )
      .subscribe({
        next: (response: CropHealthAnalysisViewModelResponseResult) => {
          this.ndmiAnnualAnalysis = response?.result.analysisData.ndmiAnnualAnalysis?.sort(
            (a, b) => a.date.toMillis() - b.date.toMillis()
          );
          this.ndviAnnualAnalysis = response?.result.analysisData.ndviAnnualAnalysis?.sort(
            (a, b) => a.date.toMillis() - b.date.toMillis()
          );
          this.gddAnalysisData = response?.result.gddAnalysisData?.sort(
            (a, b) => a.date.toMillis() - b.date.toMillis()
          );
          this.precipitationAnalysisData = response?.result.precipitationAnalysisData?.sort(
            (a, b) => a.date.toMillis() - b.date.toMillis()
          );
        },
      });
  }

  getNotes() {
    this.cropHealthService.setNotes([]);

    this.noteLoading = true;
    this.noteProxy
      .note_GetPlantation(this.selectedPlantation.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.noteLoading = false;
        })
      )
      .subscribe((res) => {
        const notes = res?.result?.reverse() ?? [];
        this.cropHealthService.setNotes(notes);

        // update pins if they are visible
        setTimeout(() => {
          if (notes?.length === 0) {
            this.cropHealthService.setShowNotePins(false);
          } else {
            this.cropHealthService.setShowNotePins(this.cropHealthService.showNotePins);
          }
        }, 100);
      });
  }

  lookup() {
    forkJoin([
      this.lookupProxy.lookup_GetNoteTags(),
      this.lookupProxy.lookup_GetNoteTypes(),
      this.lookupProxy.lookup_GetProblematicSpotStatuses(),
    ]).subscribe(([noteTagsRes, noteTypesres, statusesRes]) => {
      const noteTags = noteTagsRes?.result ?? [];
      const noteTypes = noteTypesres?.result ?? [];
      const problematicSpotStatuses = statusesRes?.result ?? [];
      this.cropHealthService.setNoteLookups({
        noteTags,
        noteTypes,
        problematicSpotStatuses
      });
    });
  }

  // This function is executed when a date is selected from the calendar 
  // in the child component (CropHealthMapCalendarComponent): it is checked whether the selected date’s image data has already been loaded.
  // If not, this function is executed and calculates the start and end date parameters to request new images from the database.
  onSelectDate(selectedDate: DateTime<boolean>) {
    // Find the index of the selected date in the list of all available dates.
    const selectedDateIndex = this.availableDates.findIndex((e) => e.hasSame(selectedDate, 'day'));

    if (selectedDateIndex !== -1) {
      // Get the index of the last available date in the array.
      const lastAvailableDayIndex = this.availableDates.length - 1;

      // Determine the start date for the new data request:
      // If there are at least 7 days ahead of the selected date, 
      // use the date 7 days after the selected one as the start date.
      // Otherwise, use the last available date as the start date.
      const startDateIndex =
        lastAvailableDayIndex - selectedDateIndex >= 7 ? selectedDateIndex + 7 : lastAvailableDayIndex;
      const startDate = this.availableDates[startDateIndex];

      // Get the date of the last image that has already been loaded.
      const lastLoadedImageDate =
        this.cropHealthData.mapList[this.cropHealthData.mapList.length - 1].imageDate;

      // Find the index of the last loaded image date in the availableDates array.
      const lastLoadedImageDateIndex = this.availableDates.findIndex((e) =>
        e.hasSame(lastLoadedImageDate, 'day')
      );

      // If the last loaded image date is not found in availableDates
      // or it is already the most recent available date,
      // it means there are no new images to load — so stop the process.
      if (lastLoadedImageDateIndex === -1 || lastLoadedImageDateIndex === this.availableDates.length - 1) {
        return;
      }

      // Otherwise, define the next date (after the last loaded one) as the end date
      // for the next data request.
      const endDateIndex = lastLoadedImageDateIndex + 1;
      const endDate = this.availableDates[endDateIndex];

      // dates are sorted DESC. so the endDateIndex should be smaller than startDate
      if (endDateIndex >= startDateIndex || !startDate || !endDate) {
        return;
      }

      // Finally, request new crop health data from the database
      // between the calculated start and end dates.
      // Pass `selectedDate` to keep track of which date triggered the load.
      this.getCropHealthData(startDate, endDate, true, selectedDate);
    }
  }

  loadMoreCropHealthData() {
    const loadedMaps = this.cropHealthData?.mapList;
    if (!loadedMaps?.length) {
      return;
    }

    // set the last date of the existing data as endDate
    const lastLoadedImageDate = loadedMaps[loadedMaps.length - 1].imageDate;

    // index of lastImageDate in availableDates
    const lastImageDateIndex = this.availableDates.findIndex((e) => e.hasSame(lastLoadedImageDate, 'day'));

    if (lastImageDateIndex === -1 || lastImageDateIndex === this.availableDates.length - 1) {
      return;
    }

    const endDateIndex = lastImageDateIndex + 1;
    const endDate = this.availableDates[endDateIndex];
    let startDate: DateTime<boolean>;
    if (this.availableDates[endDateIndex + 10]) {
      startDate = this.availableDates[endDateIndex + 10];
    } else {
      startDate = this.availableDates[this.availableDates.length - 1];
    }

    if (startDate.toMillis() < endDate.toMillis()) {
      this.getCropHealthData(startDate, endDate, true);
    }
  }

  resolveProblematicSpotMode = false;
  seeProblematicSpot() {
    // Mixpanel: this event should be triggered only when user clicks "See".
    this.trackServicesProblemSpotSeen(this.selectedDay);
    this.resolveProblematicSpotMode = true;
    this.problematicSpots = this.cropHealthData.problematicSpotView?.filter((e) =>
      e.date.hasSame(this.selectedDay.imageDate, 'day')
    );

    this.cropHealthService.setMapOverlayMessage(undefined)
  }

  onChangeDay(newDay: SatelliteViewModel) {
    if (newDay) {
      /* change the this.selectedDay if
      * 1- this.selectedDay is undefined. it means the pages is loading for first
      * 2- the mapType is changed
      * 3- the newDay and the this.selectedDay are not equal
      */
      if (!this.selectedDay || newDay.cropMapTypes !== this.selectedDay.cropMapTypes || newDay.imageDate.hasSame(this.selectedDay.imageDate, 'day')) {
        this.resolveProblematicSpotMode = false;
        this.problematicSpots = [];
        this.selectedDay = newDay;
        this.ndviNdmiDistributionData = this.selectedDay?.mapDistributions ?? [];
        this.noAnalysisIssueState = null;
      }
    }
  }

  // Mixpanel: forwarded from distribution card for Services Map Viewed.
  onNoAnalysisIssueChanged(state: { date: string; visible: boolean }): void {
    this.noAnalysisIssueState = state;
  }

  get isSplitMapLayout(): boolean {
    return this.mode === 'view' || this.mode === 'add' || this.resolveProblematicSpotMode;
  }

  changeAddNoteMode(enable: boolean, getNotes = false) {
    if (enable) {
      if (!this.permissionService.isGrantedOnUserPermissions('Note_Create')) {
        return;
      }
      // note can not be added to a cloudy day
      if (this.selectedDay?.isCloudly) {
        this.toastService.warn({ detail: 'doktar_app.services.crop_monitoring.cloudy_day_add_note_warning' });
        return;
      }
      this.mixpanelTracker.trackServicesNoteAddingStarted();
    }

    this.cropHealthService.setMode(enable ? 'add' : 'reset')
    this.resetMapSize(false, enable);
    this.cropHealthService.setMapOverlayMessage(undefined)

    if (!enable) {
      if (this.resolveProblematicSpotMode) {
        this.resolveProblematicSpotMode = false;
        this.problematicSpots = [];
        setTimeout(() => {
          this.cropHealthMapComponent.mapInstance.resize();
        }, 100);

        // get CropHealth data again
        const endDate = this.cropHealthData.mapList[0].imageDate;
        const startDate = this.cropHealthData.mapList[this.cropHealthData.mapList.length - 1].imageDate;
        this.getCropHealthData(startDate, endDate, false);
      }

      this.cropHealthMapComponent.controlMapZoomEnabled = true;
      this.cropHealthService.setShowNotePins(this.cropHealthService.showNotePins);

      this.updateMessageOverlay(this.selectedDay);
    } else {
      // disable controlMapZoom (to show/hide pins) when noteMode is enabled
      this.cropHealthMapComponent.controlMapZoomEnabled = false;

      // hide pins (if exist) from map when noteMode is enabled
      // Noted that we hide the notes directly from cropHealthMapComponent, not the service
      // the value of showNotePins in cropHealthService is not changed
      // it should keep the value to apply it when user return from add note mode
      this.cropHealthMapComponent.showNotesPins(false);
    }

    if (getNotes) {
      this.getNotes();
    }

    this.resizeMapIfFullscreen();
  }

  // when source is 'map', means the user clicked a note on the map
  // and when source is 'seeAllNote', means the user click on See all notes
  changeViewNoteMode(enable: boolean, note?: NoteViewModel, source: 'map' | 'seeAllNotes' | null = null) {
    this.selectedNoteForView = !enable ? undefined : note;

    // when user exits from view mode
    if (!enable) {
      if (this.viewNoteSource === 'seeAllNotes') {
        // if view mode was activated by clicking on See All Notes button, then CropHealthNoteAllComponent component should be displayed
        // then we need to display them again
        this.cropHealthService.setMode('seeAllNotes');
        this.cropHealthService.setShowNotePins(this.cropHealthService.showNotePins);
      } else {
        this.cropHealthService.setMode('reset');
        if (this.viewNoteSource === 'map') {
          // if view mode was activated by clicking on a note icon from the map, it means note pins were visible before clicking
          // then we need to display them again
          this.cropHealthService.setShowNotePins(true);
        }
      }

      this.cropHealthMapComponent.controlMapZoomEnabled = true;
      this.updateMessageOverlay(this.selectedDay);
    } else {
      this.trackServicesNoteViewed(note);
      this.cropHealthService.setMode('view');
      this.viewNoteSource = source;

      // disable controlMapZoom (to show/hide pins) when noteMode is enabled
      this.cropHealthMapComponent.controlMapZoomEnabled = false;
      // hide pins (if exist) from map when noteMode is enabled
      this.cropHealthMapComponent.showNotesPins(false);
    }

    setTimeout(() => {
      this.resetMapSize(false, false);
    }, 10);

    this.resizeMapIfFullscreen();
  }

  private trackServicesNoteViewed(note?: NoteViewModel): void {
    if (!note || !this.selectedPlantation) {
      return;
    }

    const land = this.plantationService.landList?.find((item) =>
      item.plantations?.some((plantation) => plantation.id === this.selectedPlantation.id)
    );
    const enterpriseIdFromLand = (land as any)?.enterpriseId as string | undefined;
    const enterpriseId = enterpriseIdFromLand || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);
    const problemType = note.noteTags?.map((item) => item.name).filter(Boolean).join(', ');

    this.mixpanelTracker.trackServicesNoteViewed({
      'note title': note.title?.trim() || undefined,
      'problem type': problemType || undefined,
      'description entered': !!note.noteContent?.trim(),
      'photo added': !!note.fileList?.length,
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
      'has premium plan': !!this.selectedPlantation.isPremium,
    });
  }

  resetMapSize(fullscreen = false, changeCursor = false) {
    const mapElement = document.querySelector('#map-content');
    if (fullscreen) {
      if (mapElement) {
        mapElement.classList.add('full-height-map');
        this.mapInstance.getCanvas().style.cursor = 'grab';
      }
    } else {
      if (mapElement) {
        mapElement.classList.remove('full-height-map');
        if (changeCursor) {
          this.mapInstance.getCanvas().style.cursor = `url('assets/svg/note-pin.svg'), auto`;
        } else {
          this.mapInstance.getCanvas().style.cursor = 'grab';
        }
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (!this.mapFullscreen) {
      return;
    }

    this.mapFullscreen = false;
    this.cropHealthMapComponent.fullScreenMode = false;
    this.scheduleMapResize();
  }

  /** Syncs parent fullscreen state with the map child and refreshes map data when entering fullscreen. */
  fullScreen() {
    this.mapFullscreen = this.cropHealthMapComponent.fullScreenMode;

    if (this.mapFullscreen) {
      const currentMapCount =
        this.cropHealthData?.mapList?.filter((e) => e.cropMapTypes === this.selectedMapType?.value)?.length ?? 0;
      if (currentMapCount < 8) {
        this.loadMoreCropHealthData();
      }
    }

    this.scheduleMapResize();
  }

  /** Defers Mapbox resize until after the fullscreen CSS/layout transition finishes. */
  private scheduleMapResize(delay = 300): void {
    setTimeout(() => {
      this.cropHealthMapComponent.mapInstance?.resize();
    }, delay);
  }

  /** Resizes the map canvas only while fullscreen is active (e.g. after note panel open/close). */
  private resizeMapIfFullscreen(delay = 300): void {
    if (!this.mapFullscreen) {
      return;
    }

    this.scheduleMapResize(delay);
  }

  addNoteMarker(latLng: LngLat) {
    this.cropHealthMapComponent.addMarker(latLng);
  }

  removeNoteMarker(_latLng: LngLat) {
    this.cropHealthMapComponent.removeMarker();
  }

  ngOnDestroy(): void {
    this.cropHealthService.reset();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
